import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { classifyWidth, useWindowWidth } from "./useSizeClass.js";
import { Icon } from "../icons/Icon.js";
import "./AppShell.css";
/*
 * Wunschbreiten der Ränder in der Größenklasse „iPad" — dieselben Zahlen wie in
 * AppShell.css (.ki-shell__nav, .ki-shell--tablet .ki-shell__list, .ki-shell__context,
 * .ki-shell__handle). Hier zusätzlich als Zahlen, weil BREAKPOINT_OVERLAY unten daraus
 * errechnet wird — bei einer Änderung der CSS-Wunschbreiten diese Zahlen mitziehen,
 * appShellOverflow.test.ts prüft nur die CSS-Seite, nicht diesen Abgleich.
 */
const NAV_WUNSCHBREITE = 200;
const LISTE_WUNSCHBREITE_IPAD = 264;
const KONTEXT_WUNSCHBREITE = 280;
const GRIFF_BREITE = 18;
/*
 * Mindestbreite, ab der die Detailspalte noch brauchbar ist. Nicht geraten, sondern aus
 * einer bereits erprobten Zahl abgeleitet: Bei Fensterbreiten unter 744px zeigt AppShell
 * (Größenklasse „phone") genau denselben Aktainhalt einspaltig und randlos an — geprüft
 * u. a. an der schmalsten Vorgabe in e2e/faltung.spec.ts (iPhone 15, 393px CSS-Breite).
 * Funktioniert der Inhalt dort auf voller Fensterbreite, ist eine etwas kleinere Spalte
 * mit demselben Inhalt auf dem iPad ebenfalls brauchbar. 360px liegt mit Sicherheitsabstand
 * unter den 393px, statt die kleinste erprobte Zahl selbst als Grenzfall zu nehmen.
 */
const DETAIL_MINDESTBREITE = 360;
/**
 * Schwelle, unterhalb derer Navigation und Kontext beim Aufklappen den Inhalt überlagern
 * statt ihn zu verdrängen (Auftraggeber-Entscheidung vom 30.08., siehe Bericht
 * .superpowers/sdd/2026-08-30-paket1-fundament/ueberlagern-report.md).
 *
 * Rechnung: Bei beiden Rändern gleichzeitig offen — dem ungünstigsten, aber jederzeit
 * möglichen Fall, da beide Griffe unabhängig voneinander bedienbar sind — verlangen
 * Navigation, Liste, Kontext und die zwei Griffe zusammen ihre Wunschbreite:
 *   200 (Navigation) + 264 (Liste, iPad) + 280 (Kontext) + 2 × 18 (Griffe) = 780px.
 * Erst was darüber hinausgeht, bekommt beim Drängen die Detailspalte (siehe
 * .superpowers/sdd/2026-08-30-paket1-fundament/ueberlauf-report.md). Damit ihr dabei
 * mindestens DETAIL_MINDESTBREITE bleibt, muss das Fenster mindestens
 *   780 + 360 = 1140px
 * breit sein — sonst wird überlagert statt gedrängt.
 *
 * Die Schwelle gilt EINHEITLICH für beide Ränder, unabhängig davon, ob nur einer oder
 * beide zugleich offen sind: Ein einzeln geöffneter Rand bräuchte rechnerisch weniger
 * Platz und könnte bis zu einer niedrigeren Breite noch drängen — aber eine von der
 * Randkombination abhängige Umschaltung würde den bereits gedrängten ersten Rand beim
 * zusätzlichen Öffnen des zweiten nachträglich in eine Überlagerung springen lassen
 * (ein Sprung im Layout mitten in der Bedienung). Eine einzige, breitenabhängige Schwelle
 * für beide Ränder ist vorhersagbar und entspricht dem iPadOS-Vorbild: kompakte
 * Breitenklassen überlagern grundsätzlich, reguläre drängen grundsätzlich.
 */
export const BREAKPOINT_OVERLAY = NAV_WUNSCHBREITE + LISTE_WUNSCHBREITE_IPAD + KONTEXT_WUNSCHBREITE + 2 * GRIFF_BREITE +
    DETAIL_MINDESTBREITE; // = 1140
/**
 * Wahr, sobald der Rahmen einmal gezeichnet wurde. Die Klasse, die daraus entsteht
 * (.ki-shell--bewegung), schaltet in AppShell.css die Übergänge frei.
 *
 * Grund für die Verzögerung: Eine CSS-Transition läuft immer dann, wenn sich ein Wert
 * ändert — auch dann, wenn er sich nur ändert, weil die Seite gerade erst entsteht. Ohne
 * diesen Riegel könnten die Ränder beim Start sichtbar hineinfahren, obwohl niemand etwas
 * bedient hat. Die Freigabe passiert erst im nächsten Bild (requestAnimationFrame statt
 * direkt im Effekt), damit der erste Zustand garantiert schon gemalt ist, bevor Bewegung
 * überhaupt möglich wird.
 */
function useBewegungNachErstemBild() {
    const [erlaubt, setErlaubt] = useState(false);
    useEffect(() => {
        const kennung = requestAnimationFrame(() => setErlaubt(true));
        return () => cancelAnimationFrame(kennung);
    }, []);
    return erlaubt;
}
/**
 * Der gefaltete Rahmen. Jede Ansicht wird einmal gebaut und sagt nur, in welchen Bereich
 * sie gehört — die Faltung passiert hier und nirgends sonst. Neue Module erben das Verhalten.
 *
 * Der Zustand der Klappen wird NICHT hier gehalten, sondern nach außen gemeldet:
 * Er gehört zum Benutzer und wird in der Datenbank gespeichert.
 */
export function AppShell(p) {
    const breite = useWindowWidth();
    const klasse = classifyWidth(breite);
    const bewegungErlaubt = useBewegungNachErstemBild();
    const desktop = klasse === "desktop";
    const navOffen = desktop || p.panels.nav;
    const kontextOffen = desktop || p.panels.context;
    // Unterhalb der Schwelle überlagern offene Ränder den Inhalt, statt ihn zu verdrängen
    // (siehe BREAKPOINT_OVERLAY oben). Gilt nur auf dem iPad — Desktop drängt/überlagert nie
    // (alle vier Bereiche stehen dort dauerhaft nebeneinander), Telefon hat keine Ränder.
    const ueberlagert = klasse === "tablet" && breite < BREAKPOINT_OVERLAY;
    const navUeberlagert = ueberlagert && navOffen;
    const kontextUeberlagert = ueberlagert && kontextOffen;
    /*
     * Escape schließt einen überlagernden Rand — die erwartete Geste, wenn sich etwas über
     * den Inhalt gelegt hat. Zwei Grenzen sind dabei bewusst gesetzt:
     *
     *  1. NUR im überlagernden Zustand. Drängt der Rand (ab BREAKPOINT_OVERLAY) oder steht er
     *     auf dem Desktop dauerhaft, ist er Teil des Layouts und kein Overlay; er dürfte dann
     *     auf Escape nicht verschwinden. Deshalb hängt der Horcher an navUeberlagert /
     *     kontextUeberlagert und existiert sonst gar nicht.
     *  2. KEINE Fokusfalle. Ein überlagernder Rand ist ein Bereich, kein Dialog — der Fokus
     *     wird weder hineingezogen noch festgehalten (Entscheidung aus Paket 1). Die
     *     Fokusfalle in overlay/useFocusTrap.ts bleibt echten Dialogen vorbehalten.
     *
     * Der Horcher hängt am Dokument, nicht am Rahmen: Er soll auch greifen, wenn der Fokus
     * gerade nirgendwo (auf <body>) steht — genau der Normalfall, nachdem ein Rand per
     * Zeigergeste geöffnet wurde. Er horcht in der Blasenphase, damit ein echter Dialog
     * darüber (App.tsx nutzt useFocusTrap, das bei Escape stopPropagation ruft) Vorrang
     * behält und nicht zugleich der Rand geschlossen wird.
     *
     * Sind beide Ränder offen, schließt Escape beide — dieselbe Meldung wie beim Klick auf
     * den abdunkelnden Hintergrund, und derselbe Grund: Der Hintergrund liegt hinter beiden.
     */
    const onPanelsChangeRef = useRef(p.onPanelsChange);
    onPanelsChangeRef.current = p.onPanelsChange;
    useEffect(() => {
        if (!navUeberlagert && !kontextUeberlagert)
            return;
        function beiTaste(ereignis) {
            if (ereignis.key !== "Escape")
                return;
            onPanelsChangeRef.current({ nav: false, context: false });
        }
        document.addEventListener("keydown", beiTaste);
        return () => document.removeEventListener("keydown", beiTaste);
        // onPanelsChange bewusst NICHT in dieser Liste — die Identität eines Rückrufs, den
        // Aufrufer fast immer als Inline-Funktion übergeben, würde den Horcher bei jedem
        // Neuzeichnen ab- und wieder anmelden (siehe dieselbe Überlegung in useFocusTrap.ts).
    }, [navUeberlagert, kontextUeberlagert]);
    if (klasse === "phone") {
        return (_jsxs("div", { className: "ki-shell ki-shell--phone", children: [_jsxs("header", { className: "ki-shell__header ki-shell__header--sticky", children: [!p.showList && (_jsx("button", { type: "button", className: "ki-shell__back", "aria-label": "Zur\u00FCck zur Liste", onClick: p.onBack, children: _jsx(Icon, { name: "chevron-left", size: 18 }) })), p.header] }), _jsx("main", { className: "ki-shell__main", children: p.showList ? p.list : p.detail }), _jsx("nav", { className: "ki-shell__tabbar", children: p.tabBar })] }));
    }
    // iPadOS-übliches Verhalten: Ein abdunkelnder Hintergrund liegt hinter den überlagernden
    // Rändern; ein Klick daneben schließt sie. Der Klappenzustand wird dabei — wie immer —
    // nur über onPanelsChange nach außen gemeldet, nicht hier gehalten (siehe Kopfkommentar).
    function schliesseUeberlagerung() {
        p.onPanelsChange({ nav: false, context: false });
    }
    /*
     * Genau an BREAKPOINT_OVERLAY wechselt ein offener Rand zwischen Drängen und Überlagern —
     * die Akte wird dabei mit beiden Rändern offen um bis zu 480px breiter oder schmaler.
     * Diese Klasse gibt dafür den weichen Übergang frei (siehe AppShell.css); ausgeführt wird
     * er ausschließlich in CSS, samt Rücksicht auf prefers-reduced-motion.
     */
    const rahmenKlassen = `ki-shell ki-shell--${klasse}`
        + (bewegungErlaubt ? " ki-shell--bewegung" : "");
    return (_jsxs("div", { className: rahmenKlassen, children: [_jsx("header", { className: "ki-shell__header", children: p.header }), _jsxs("div", { className: "ki-shell__body", children: [(navUeberlagert || kontextUeberlagert) && (_jsx("div", { className: "ki-shell__scrim", "aria-hidden": "true", onClick: schliesseUeberlagerung })), navOffen && (_jsx("aside", { className: navUeberlagert ? "ki-shell__nav ki-shell__nav--overlay" : "ki-shell__nav", children: p.nav })), !desktop && (_jsx("button", { type: "button", className: "ki-shell__handle ki-shell__handle--left", "aria-label": p.panels.nav ? "Navigation ausblenden" : "Navigation einblenden", "aria-expanded": p.panels.nav, onClick: () => p.onPanelsChange({ ...p.panels, nav: !p.panels.nav }), children: _jsx(Icon, { name: p.panels.nav ? "chevron-left" : "chevron-right", size: 14 }) })), _jsx("section", { className: "ki-shell__list", children: p.list }), _jsx("section", { className: "ki-shell__detail", children: p.detail }), !desktop && (_jsx("button", { type: "button", className: "ki-shell__handle ki-shell__handle--right", "aria-label": p.panels.context ? "Kontext ausblenden" : "Kontext einblenden", "aria-expanded": p.panels.context, onClick: () => p.onPanelsChange({ ...p.panels, context: !p.panels.context }), children: _jsx(Icon, { name: p.panels.context ? "chevron-right" : "chevron-left", size: 14 }) })), kontextOffen && (_jsx("aside", { className: kontextUeberlagert ? "ki-shell__context ki-shell__context--overlay" : "ki-shell__context", children: p.context }))] })] }));
}
//# sourceMappingURL=AppShell.js.map